import { UserStore } from '../../../stores/UserStore';
import {
  EventInstance,
  EventInstanceParticipant,
  EventStatus,
  EventTemplate,
  EventTemplateParticipant,
} from '../api/types';
import {
  Participant,
  ParticipantStatus,
  Training,
  TrainingStatus,
} from '../types/calendarTypes';

export const getEventStatusView = (
  status: EventStatus,
  t: (key: string) => string
) => {
  switch (status) {
    case 'Active':
      return {
        bg: 'bg-green-50 border-green-200',
        icon: '✅',
        text: t('training.status.active'),
      };
    case 'Cancelled':
      return {
        bg: 'bg-red-50 border-red-200',
        icon: '❌',
        text: t('training.status.cancelled'),
      };
    case 'Finished':
      return {
        bg: 'bg-blue-50 border-blue-200',
        icon: '✅',
        text: t('training.status.completed'),
      };
    default:
      return {
        bg: 'bg-gray-50 border-gray-200',
        icon: '❓',
        text: t('training.status.active'),
      };
  }
};

export const getEventStatusByParticipantsView = (
  participants: Participant[],
  t: (key: string) => string
) => {
  // Group participants by status
  const statusGroups = participants.reduce(
    (groups, participant) => {
      const status = participant.status;
      if (!groups[status]) {
        groups[status] = [];
      }
      groups[status].push(participant);
      return groups;
    },
    {} as Record<string, Participant[]>
  );

  // Check conditions using group lengths
  if (statusGroups['Cancelled']?.length > 0) {
    return {
      bg: 'bg-orange-50 border-orange-200',
      icon: '🟠',
      text: t('training.status.partial_cancelled'),
    };
  }

  if (statusGroups['Pending']?.length > 0) {
    return {
      bg: 'bg-yellow-50 border-yellow-200',
      icon: '⏳',
      text: t('training.status.waiting'),
    };
  }

  const confirmedCount =
    (statusGroups['Confirmed']?.length || 0) +
    (statusGroups['ConfirmedBySystem']?.length || 0);
  const replacedCount = statusGroups['Replaced']?.length || 0;

  if (
    confirmedCount + replacedCount === participants.length &&
    participants.length > 0
  ) {
    return {
      bg: 'bg-green-50 border-green-200',
      icon: '✅',
      text: t('training.status.confirmed'),
    };
  }

  return {
    bg: 'bg-yellow-50 border-yellow-200',
    icon: '⏳',
    text: t('training.status.waiting'),
  };
};

export const formatHHmm = (timeStr?: string) => {
  if (!timeStr) return '';

  // Check if it's already in HH:mm format
  if (/^\d{2}:\d{2}$/.test(timeStr)) {
    return timeStr;
  }

  // Try to parse as ISO datetime
  try {
    const date = new Date(timeStr);
    if (Number.isNaN(date.getTime())) {
      console.warn('Invalid date:', timeStr);
      return '';
    }
    return date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch (error) {
    console.error('Error parsing date:', timeStr, error);
    return '';
  }
};

/**
 * Determines training level based on participants' skill levels
 */
export const getTrainingLevel = (
  participants: Participant[],
  userStore: UserStore
) => {
  let trainingLevelWeight = 1;
  let trainingLevelName: string | null = null;
  let trainingLevelIconId: string | undefined;

  if (participants.length > 0) {
    const participantWeights = participants
      .map((p) => userStore.getUserSkillLevelWeight(p.id))
      .filter((w): w is number => w !== null);

    if (participantWeights.length === 0) {
      return null;
    }

    const minWeight = Math.min(...participantWeights);
    const minWeightParticipant = participants.find(
      (p) => userStore.getUserSkillLevelWeight(p.id) === minWeight
    );

    if (minWeightParticipant) {
      trainingLevelWeight = minWeight;
      trainingLevelName = userStore.getUserSkillLevel(minWeightParticipant.id);
      trainingLevelIconId = userStore.getUserSkillLevelIconId(
        minWeightParticipant.id
      );
    }
  }

  return { trainingLevelWeight, trainingLevelName, trainingLevelIconId };
};

/**
 * Creates a Participant object from EventInstanceParticipant
 */
export const createParticipantFromInstance = (
  participant: EventInstanceParticipant,
  instanceStatus: string,
  userStore: UserStore
): Participant => {
  const replacingUser = participant.replacingUserId
    ? userStore.users.get(participant.replacingUserId)
    : undefined;

  return {
    id: participant.userId,
    name:
      userStore.users.get(participant.userId)?.name ||
      `User ${participant.userId}`,
    photoUrl: userStore.users.get(participant.userId)?.photoUrl || undefined,
    status: instanceStatus === 'Cancelled' ? 'Cancelled' : participant.status,
    replacementFor: participant.replacingUserId || undefined,
    replacing: replacingUser
      ? {
          id: replacingUser.id,
          name: replacingUser.name,
          photoUrl: replacingUser.photoUrl || undefined,
        }
      : undefined,
    cancelledWithValidExcuse: participant.cancelledWithValidExcuse ?? undefined,
  };
};

/**
 * Creates a Participant object from EventTemplateParticipant
 */
export const createParticipantFromTemplate = (
  participant: EventTemplateParticipant,
  userStore: UserStore
): Participant => ({
  id: participant.userId,
  name:
    userStore.users.get(participant.userId)?.name ||
    `User ${participant.userId}`,
  photoUrl: userStore.users.get(participant.userId)?.photoUrl || undefined,
  status: 'Pending' as ParticipantStatus,
  replacementFor: undefined,
});

/**
 * Converts EventInstance to Training format
 */
export const convertInstanceToTraining = (
  instance: EventInstance,
  dateStr: string,
  userStore: UserStore,
  formatDate: (date: Date, format: string) => string
): Training => {
  // Convert EventInstanceParticipant to Participant format
  const uiParticipants = (instance.participants || []).map((participant) =>
    createParticipantFromInstance(participant, instance.status, userStore)
  );

  // Check if current user is participant
  const isCurrentUserParticipant = uiParticipants.some(
    (p: Participant) => p.id === userStore.userId
  );

  // Determine training level based on participants
  const { trainingLevelWeight, trainingLevelName, trainingLevelIconId } =
    getTrainingLevel(uiParticipants, userStore) ?? {};

  return {
    id: instance.id,
    templateId: instance.templateId,
    startDateTime: new Date(instance.startTime),
    endDateTime: new Date(instance.endTime),
    startTime: formatDate(new Date(instance.startTime), 'HH:mm'),
    endTime: formatDate(new Date(instance.endTime), 'HH:mm'),
    date: dateStr,
    participants: uiParticipants,
    level: trainingLevelWeight,
    levelName: trainingLevelName ?? undefined,
    levelIconId: trainingLevelIconId,
    status: instance.status,
    currentUserId: userStore.userId || undefined,
    currentUserRoles: userStore.user?.roles,
    isCurrentUserParticipant,
  };
};

/**
 * Converts EventTemplate to Training format
 */
export const convertTemplateToTraining = (
  template: EventTemplate,
  date: Date,
  userStore: UserStore
): Training => {
  const startDateTime = new Date(template.startTime);
  const endDateTime = new Date(template.endTime);

  // Set the date to the target date while keeping the time
  startDateTime.setFullYear(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  endDateTime.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());

  // Convert template participants to UI format
  const uiParticipants = (template.participants || []).map((participant) =>
    createParticipantFromTemplate(participant, userStore)
  );

  // Check if current user is participant
  const isCurrentUserParticipant = uiParticipants.some(
    (p: Participant) => p.id === userStore.userId
  );

  // Determine training level based on participants
  const { trainingLevelWeight, trainingLevelName, trainingLevelIconId } =
    getTrainingLevel(uiParticipants, userStore) ?? {};

  return {
    id: template.id,
    startDateTime,
    endDateTime,
    startTime: startDateTime.toTimeString().slice(0, 5), // "HH:MM"
    endTime: endDateTime.toTimeString().slice(0, 5), // "HH:MM"
    date: date.toISOString().split('T')[0], // "YYYY-MM-DD"
    participants: uiParticipants,
    level: trainingLevelWeight,
    levelName: trainingLevelName ?? undefined,
    levelIconId: trainingLevelIconId,
    status: 'Active' as TrainingStatus,
    currentUserId: userStore.userId || undefined,
    currentUserRoles: userStore.user?.roles || undefined,
    isCurrentUserParticipant,
    isTemplate: true, // Flag to identify templates
  };
};
